import { IconStatus, InvestorBadge, SuitabilityBadge, Text, HStack } from '@genial/components';

import { CustomerDetailFullAccount } from '../../../../../../store/customerDetail/types';
import { CustomerDuckInfoProps } from '../InfoContainer/components/CustomerDuckInfo';
import {
  CustomerDuckProductEnable,
  CustomerDuckProductEnableLoading,
} from '../InfoContainer/components/CustomerDuckProductEnable';
import {
  CustomerDuckWarning,
  CustomerDuckWarningLoading,
} from '../InfoContainer/components/CustomerDuckWarning';

export const tooltipAccountNumber = 'Código do cliente no Sinacor da corretora Investimentos.';
export const tooltipPluralAccountNumber = 'Código do cliente por conta na corretora Institucional.';
export const tooltipDigitalAccountNumber = 'Código do cliente no banco (matera).';
export const tooltipPnpAccountNumber =
  'Código do cliente no Sinacor da corretora Institucional. Todas as operações são realizados por essa conta';

export const getAccountAndIncomeInfos = (
  customerDetail: CustomerDetailFullAccount | null | undefined,
  isLoading: boolean
): CustomerDuckInfoProps[] => {
  return [
    {
      label: 'Cod. Conta Digital',
      value: customerDetail?.accountAndEarnings.digitalAccountNormalized,
      labelTooltip: tooltipDigitalAccountNumber,
      loading: isLoading,
    },
    {
      label: 'Cod. Conta de Custódia',
      value: customerDetail?.accountAndEarnings.pnpAccountNormalized,
      labelTooltip: tooltipPnpAccountNumber,
      loading: isLoading,
    },
    {
      label: 'Cod. Conta Plural',
      value: customerDetail?.accountAndEarnings.pluralAccountNormalized,
      labelTooltip: tooltipPluralAccountNumber,
      loading: isLoading,
    },
    {
      label: 'Cod. Conta Sinacor',
      value: customerDetail?.accountAndEarnings.sinacorAccountNormalized,
      labelTooltip: tooltipAccountNumber,
      loading: isLoading,
      opaque: true,
    },
    {
      label: 'Perfil Investimento',
      value: (
        <HStack>
          <InvestorBadge
            investorType={customerDetail?.investorProfile.investmentProfileNormalized}
          />
        </HStack>
      ),
      mobileValue: customerDetail?.investorProfile.investmentProfileTextNormalized,
      loading: isLoading,
    },
    {
      label: 'Perfil de risco',
      value: customerDetail?.investorProfile.riskProfileDescriptionNormalized,
      loading: isLoading,
    },
    {
      label: 'Renda mensal',
      value: customerDetail?.accountAndEarnings.monthlyIncomeNormalized,
      loading: isLoading,
    },
    {
      label: 'Renda anual',
      value: customerDetail?.accountAndEarnings.yearlyIncomeNormalized,
      loading: isLoading,
    },
    {
      label: 'Patrimônio',
      value: customerDetail?.accountAndEarnings.netWorthNormalized,
      loading: isLoading,
    },
    {
      label: 'Outros rendimentos',
      value: customerDetail?.accountAndEarnings.otherIncomeNormalized,
      loading: isLoading,
    },
    {
      label: 'Data de Cadastro',
      value: customerDetail?.accountAndEarnings.registrationDateNormalized,
      valueRightIcon: 'success',
      loading: isLoading,
    },
    {
      label: 'Data de recadastro',
      value: customerDetail?.accountAndEarnings.updateDateNormalized,
      valueRightIcon: 'success',
      loading: isLoading,
    },
  ];
};

export const getBlockingReasonsInfo = (
  customerDetail: CustomerDetailFullAccount | null | undefined,
  isLoading: boolean
): CustomerDuckInfoProps[] => {
  if (isLoading) {
    return Array.from({ length: 2 }).map(() => ({
      value: undefined,
      loading: true,
      loadingCustomElement: <CustomerDuckWarningLoading />,
    }));
  }

  if (!customerDetail?.pendingReasons?.length) {
    return [
      {
        value: 'Este cliente não possui bloqueios no cadastro',
      },
    ];
  }

  return customerDetail.pendingReasons.map((reason) => ({
    value: <CustomerDuckWarning message={reason} />,
  }));
};

export const getSuitabilityInfo = (
  customerDetail: CustomerDetailFullAccount | null | undefined,
  isLoading: boolean
): CustomerDuckInfoProps[] => {
  return [
    {
      label: 'Status',
      loading: isLoading,
      mobileValue: customerDetail?.investorProfile.suitabilityStatusNormalized,
      value: (
        <HStack alignItems="center" space="stack.quarck">
          <IconStatus
            testId="icon-status"
            icon={customerDetail?.investorProfile.suitabilityStatusIcon.name || 'Info'}
            bgColor={
              customerDetail?.investorProfile.suitabilityStatusIcon.backgroundColor ||
              'brand.tertiary.lightest'
            }
            color={
              (customerDetail?.investorProfile.suitabilityStatusIcon.color as string) ||
              'brand.tertiary.principal'
            }
            size={customerDetail?.investorProfile.suitabilityStatusIcon.size || 'xs'}
          />
          <Text>{customerDetail?.investorProfile.suitabilityStatusNormalized}</Text>
        </HStack>
      ),
    },
    {
      label: 'Perfil',
      loading: isLoading,
      mobileValue: customerDetail?.investorProfile.suitabilityNormalized,
      value: (
        <SuitabilityBadge label={customerDetail?.investorProfile.suitabilityNormalized || ''} />
      ),
    },
    {
      label: 'Validade',
      loading: isLoading,
      value: customerDetail?.investorProfile.suitabilityValidityText,
    },
    {
      label: 'Vencimento',
      loading: isLoading,
      value: customerDetail?.investorProfile.suitabilityDueDateNormalized,
      valueRightIcon: 'success',
    },
    {
      label: 'Situação',
      loading: isLoading,
      value: customerDetail?.investorProfile.suitabilityValidityNormalized,
    },
  ];
};

export const getAbleToOperateInfo = (
  customerDetail: CustomerDetailFullAccount | null | undefined,
  isLoading: boolean
): CustomerDuckInfoProps[] => {
  const productsToOperate = customerDetail?.productsToOperate || [];

  if (isLoading) {
    return Array.from({ length: 3 }).map(() => ({
      value: undefined,
      loading: true,
      loadingCustomElement: <CustomerDuckProductEnableLoading />,
    }));
  }

  if (productsToOperate.length === 0) {
    return [
      {
        value: 'Este cliente não está habilitado a operar em nenhum produto',
      },
    ];
  }

  return productsToOperate.map((product) => ({
    value: (
      <CustomerDuckProductEnable
        description={product.productName}
        enabled={product.enabled}
        icon={product.icon}
      />
    ),
    loading: false,
  }));
};
